import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_ICONS = {
  pdf: "📄",
  video: "🎬",
  article: "📰",
  website: "🌐",
  book: "📚"
};

const TYPE_COLORS = {
  pdf: "#ff6b6b",
  video: "#4ecdc4",
  article: "#45b7d1",
  website: "#96ceb4",
  book: "#dda0dd"
};

function ResourceLibrary({ user, isAdmin }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Form state for adding resources
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    resource_type: "pdf",
    subject_id: "bible-study",
    url: "",
    thumbnail_url: ""
  });

  useEffect(() => {
    fetchResources();
  }, [selectedSubject, selectedType]);

  const fetchResources = async () => {
    try {
      const token = localStorage.getItem("token");
      let url = `${API}/resources`;
      const params = new URLSearchParams();
      if (selectedSubject) params.append("subject_id", selectedSubject);
      if (selectedType) params.append("resource_type", selectedType);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResources(response.data.resources || []);
      setFilters(response.data.filters);
    } catch (err) {
      console.error("Error fetching resources:", err);
      setError("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    if (!formData.title || !formData.url) {
      setError("Title and URL are required");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/resources`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Resource added successfully!");
      setShowAddForm(false);
      setFormData({
        title: "",
        description: "",
        resource_type: "pdf",
        subject_id: "bible-study",
        url: "",
        thumbnail_url: ""
      });
      fetchResources();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add resource");
    }
  };

  const handleDeleteResource = async (resourceId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Resource deleted");
      fetchResources();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete resource");
    }
  };

  const openResource = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const clearFilters = () => {
    setSelectedSubject("");
    setSelectedType("");
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📚</div>
        <p style={{ color: "#FFD700" }}>Loading Resource Library...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="card" data-testid="resource-library-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          <div>
            <h3 style={{ margin: "0 0 5px 0" }}>📚 Free Resource Library</h3>
            <p style={{ color: "#aaa", margin: 0 }}>
              Access free educational materials, videos, and articles
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              data-testid="add-resource-btn"
              style={{
                background: showAddForm ? "#666" : "#FFD700",
                color: showAddForm ? "#fff" : "#000",
                padding: "10px 20px",
                marginTop: 0
              }}
            >
              {showAddForm ? "Cancel" : "+ Add Resource"}
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && <div className="alert alert-error" data-testid="error-message">{error}</div>}
      {success && <div className="alert alert-success" style={{ background: "#1a4d1a", border: "1px solid #90EE90", color: "#90EE90", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>{success}</div>}

      {/* Add Resource Form (Admin Only) */}
      {showAddForm && isAdmin && (
        <div className="card" style={{ background: "#1a1a2a", borderLeft: "4px solid #FFD700" }} data-testid="add-resource-form">
          <h4 style={{ color: "#FFD700", marginBottom: "20px" }}>Add New Resource</h4>
          <form onSubmit={handleAddResource}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Resource title"
                  data-testid="resource-title-input"
                  style={{ width: "100%", padding: "10px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff" }}
                />
              </div>
              
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>URL *</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://..."
                  data-testid="resource-url-input"
                  style={{ width: "100%", padding: "10px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff" }}
                />
              </div>
              
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>Type</label>
                <select
                  value={formData.resource_type}
                  onChange={(e) => setFormData({...formData, resource_type: e.target.value})}
                  data-testid="resource-type-select"
                  style={{ width: "100%", padding: "10px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff" }}
                >
                  <option value="pdf">📄 PDF Document</option>
                  <option value="video">🎬 Video</option>
                  <option value="article">📰 Article</option>
                  <option value="website">🌐 Website</option>
                  <option value="book">📚 Book</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>Subject</label>
                <select
                  value={formData.subject_id}
                  onChange={(e) => setFormData({...formData, subject_id: e.target.value})}
                  data-testid="resource-subject-select"
                  style={{ width: "100%", padding: "10px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff" }}
                >
                  {filters?.subjects?.map(subject => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div style={{ marginTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this resource..."
                data-testid="resource-description-input"
                rows={3}
                style={{ width: "100%", padding: "10px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff", resize: "vertical" }}
              />
            </div>
            
            <button
              type="submit"
              data-testid="submit-resource-btn"
              style={{ marginTop: "15px", background: "#90EE90", color: "#000" }}
            >
              Add to Library
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ background: "#1a1a1a" }}>
        <div style={{ display: "flex", gap: "15px", flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <label style={{ display: "block", marginBottom: "5px", color: "#aaa", fontSize: "0.85rem" }}>Filter by Subject</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              data-testid="filter-subject"
              style={{ padding: "8px 15px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff", minWidth: "180px" }}
            >
              <option value="">All Subjects</option>
              {filters?.subjects?.map(subject => (
                <option key={subject.id} value={subject.id}>
                  {subject.name} ({filters.subject_counts?.[subject.id] || 0})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: "block", marginBottom: "5px", color: "#aaa", fontSize: "0.85rem" }}>Filter by Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              data-testid="filter-type"
              style={{ padding: "8px 15px", background: "#222", border: "1px solid #444", borderRadius: "6px", color: "#fff", minWidth: "150px" }}
            >
              <option value="">All Types</option>
              {filters?.types?.map(type => (
                <option key={type} value={type}>
                  {TYPE_ICONS[type]} {type.charAt(0).toUpperCase() + type.slice(1)} ({filters.type_counts?.[type] || 0})
                </option>
              ))}
            </select>
          </div>
          
          {(selectedSubject || selectedType) && (
            <button
              onClick={clearFilters}
              style={{ padding: "8px 15px", background: "#333", marginTop: "auto" }}
            >
              Clear Filters
            </button>
          )}
          
          <div style={{ marginLeft: "auto", color: "#aaa" }}>
            {resources.length} resource{resources.length !== 1 ? "s" : ""} found
          </div>
        </div>
      </div>

      {/* Resource Grid */}
      {resources.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "3rem", marginBottom: "15px" }}>📭</div>
          <h4 style={{ color: "#FFD700" }}>No Resources Yet</h4>
          <p style={{ color: "#aaa" }}>
            {isAdmin 
              ? "Click 'Add Resource' to start building the library."
              : "Check back later for free educational materials."
            }
          </p>
        </div>
      ) : (
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "20px",
          marginTop: "20px"
        }}>
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="card"
              data-testid={`resource-card-${resource.id}`}
              style={{
                background: "#1a1a1a",
                borderLeft: `4px solid ${TYPE_COLORS[resource.resource_type] || "#FFD700"}`,
                padding: "20px",
                display: "flex",
                flexDirection: "column"
              }}
            >
              {/* Resource Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                <span style={{ 
                  fontSize: "2rem",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
                }}>
                  {TYPE_ICONS[resource.resource_type] || "📄"}
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <span style={{
                    fontSize: "0.75rem",
                    background: TYPE_COLORS[resource.resource_type] || "#444",
                    color: "#000",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "bold"
                  }}>
                    {resource.resource_type?.toUpperCase()}
                  </span>
                </div>
              </div>
              
              {/* Title */}
              <h4 style={{ 
                color: "#FFD700", 
                margin: "0 0 8px 0",
                fontSize: "1.1rem",
                lineHeight: "1.3"
              }}>
                {resource.title}
              </h4>
              
              {/* Subject Badge */}
              <div style={{
                fontSize: "0.8rem",
                color: "#87CEEB",
                marginBottom: "10px"
              }}>
                {resource.subject_name || resource.subject_id}
              </div>
              
              {/* Description */}
              {resource.description && (
                <p style={{ 
                  color: "#aaa", 
                  fontSize: "0.9rem",
                  margin: "0 0 15px 0",
                  flex: 1,
                  lineHeight: "1.5"
                }}>
                  {resource.description.length > 150 
                    ? resource.description.substring(0, 150) + "..." 
                    : resource.description
                  }
                </p>
              )}
              
              {/* Meta Info */}
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between",
                fontSize: "0.75rem",
                color: "#999",
                marginBottom: "15px"
              }}>
                <span>Added by {resource.created_by}</span>
                <span>{resource.views || 0} views</span>
              </div>
              
              {/* Actions */}
              <div style={{ display: "flex", gap: "10px", marginTop: "auto" }}>
                <button
                  onClick={() => openResource(resource.url)}
                  data-testid={`open-resource-${resource.id}`}
                  style={{
                    flex: 1,
                    background: TYPE_COLORS[resource.resource_type] || "#FFD700",
                    color: "#000",
                    padding: "10px",
                    fontWeight: "bold",
                    marginTop: 0
                  }}
                >
                  {resource.resource_type === "video" ? "Watch" : 
                   resource.resource_type === "pdf" ? "View PDF" : "Open"}
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleDeleteResource(resource.id, resource.title)}
                    data-testid={`delete-resource-${resource.id}`}
                    style={{
                      background: "#4d1a1a",
                      color: "#ff6b6b",
                      padding: "10px 15px",
                      marginTop: 0
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="card" style={{ background: "#0a0a0a", marginTop: "20px" }}>
        <h4 style={{ color: "#FFD700", marginBottom: "10px" }}>
          ℹ️ About the Resource Library
        </h4>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa" }}>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Free Access:</strong> All resources are freely available - no additional cost
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Multiple Formats:</strong> PDFs, videos, articles, and external websites
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Organized:</strong> Filter by subject or resource type to find what you need
          </li>
          <li style={{ background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#eee" }}>Curated:</strong> Resources are carefully selected by administrators
          </li>
        </ul>
      </div>
    </div>
  );
}

export default ResourceLibrary;
